/**
 * Streaming event types matching the Agent Builder backend StreamingEventType enum.
 * These represent the different phases and events during agent processing.
 */
export type StreamingEventType =
  | 'StreamStarted'
  | 'StreamCompleted'
  | 'StreamError'
  | 'Thinking'
  | 'TextDelta'
  | 'TextComplete'
  | 'ToolCallStarted'
  | 'ToolCallProgress'
  | 'ToolCallCompleted'
  | 'ToolCallError'
  | 'SubAgentInvoked'
  | 'SubAgentEvent'
  | 'SubAgentCompleted'
  | 'MfeDetected'
  | 'DataInstanceSelected';

/**
 * Base streaming event structure matching the Agent Builder backend StreamingEvent record.
 */
export interface StreamingEvent {
  readonly id: string;
  readonly type: StreamingEventType;
  readonly timestamp: string;
  readonly conversation_id?: string;
  readonly sequence: number;
  readonly data: StreamingEventData;
}

export interface StreamStartedEventData {
  readonly conversation_id: string;
  readonly agent_id: string;
  readonly agent_name?: string;
}

export interface ThinkingEventData {
  readonly message?: string;
}

export interface TextDeltaEventData {
  readonly delta: string;
  readonly accumulated_text: string;
}

export interface TextCompleteEventData {
  readonly full_text: string;
}

export interface ToolCallStartedEventData {
  readonly tool_name: string;
  readonly call_id: string;
  readonly arguments?: Readonly<Record<string, unknown>>;
}

export interface ToolCallProgressEventData {
  readonly tool_name: string;
  readonly call_id: string;
  readonly progress_message: string;
  readonly progress_percentage?: number;
}

export interface ToolCallCompletedEventData {
  readonly tool_name: string;
  readonly call_id: string;
  readonly result?: string;
  readonly duration_ms: number;
  readonly status: 'success' | 'error';
}

export interface ToolCallErrorEventData {
  readonly tool_name: string;
  readonly call_id: string;
  readonly error_message: string;
  readonly is_retryable: boolean;
}

export interface SubAgentInvokedEventData {
  readonly sub_agent_id: string;
  readonly sub_agent_name?: string;
  readonly call_id: string;
  readonly input_message?: string;
}

export interface SubAgentCompletedEventData {
  readonly sub_agent_id: string;
  readonly sub_agent_name?: string;
  readonly call_id: string;
  readonly result?: string;
  readonly duration_ms: number;
}

export interface MfeDetectedEventData {
  readonly mfe: MfeInstruction;
}

export interface MfeInstruction {
  readonly name: string;
  readonly parameters: Readonly<Record<string, unknown>>;
  readonly version?: string;
  readonly shell?: string;
}

export interface StreamCompletedEventData {
  readonly response: AgentChatResponse;
}

export interface StreamErrorEventData {
  readonly error_code: string;
  readonly error_message: string;
  readonly is_recoverable: boolean;
}

/** The full chat response returned inside StreamCompleted events. */
export interface AgentChatResponse {
  readonly response: string;
  readonly tools_used: readonly string[];
  readonly suggested_prompts: readonly string[];
  readonly execution_details?: readonly ToolExecutionDetail[];
  readonly mfe?: MfeInstruction;
}

export interface ToolExecutionDetail {
  readonly operation_name: string;
  readonly timestamp: string;
  readonly duration_ms?: number;
  readonly status: 'success' | 'error' | 'validation_error';
  readonly error_message?: string;
}

/** Union type for all event data payloads. */
export type StreamingEventData =
  | StreamStartedEventData
  | ThinkingEventData
  | TextDeltaEventData
  | TextCompleteEventData
  | ToolCallStartedEventData
  | ToolCallProgressEventData
  | ToolCallCompletedEventData
  | ToolCallErrorEventData
  | SubAgentInvokedEventData
  | SubAgentCompletedEventData
  | MfeDetectedEventData
  | StreamCompletedEventData
  | StreamErrorEventData;

export interface StreamingToolCall {
  readonly tool_name: string;
  readonly call_id: string;
  readonly status: 'pending' | 'running' | 'completed' | 'error';
  readonly result?: string;
  readonly error_message?: string;
  readonly duration_ms?: number;
}

export interface StreamingState {
  readonly isStreaming: boolean;
  readonly conversationId?: string;
  readonly currentText: string;
  readonly thinkingMessage: string | null;
  readonly toolCalls: readonly StreamingToolCall[];
  readonly mfe: MfeInstruction | null;
  readonly error: StreamErrorEventData | null;
}

export const INITIAL_STREAMING_STATE: StreamingState = {
  isStreaming: false,
  conversationId: undefined,
  currentText: '',
  thinkingMessage: null,
  toolCalls: [],
  mfe: null,
  error: null,
};
